import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { movies } from "@/mocks/movies";
import { filterAndSortMovies, type MovieFilters } from "@/utils/movie";
import SearchBox from "@/components/feature/SearchBox";
import MovieCard from "@/components/feature/MovieCard";
import FilterBar from "@/components/feature/FilterBar";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import { useSeo } from "@/hooks/useSeo";

const HISTORY_KEY = "yellow-blockbuster-search-history";

function readHistory(): string[] {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? (parsed as string[]) : [];
  } catch {
    return [];
  }
}

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = searchParams.get("q") ?? "";
  const [filters, setFilters] = useState<MovieFilters>({ sort: "heat" });
  const [history, setHistory] = useState<string[]>([]);

  useEffect(() => {
    setHistory(readHistory());
  }, []);

  useEffect(() => {
    setFilters({ sort: "heat" });
  }, [keyword]);

  useSeo({
    title: keyword ? `“${keyword}”搜索结果 - 黄色大片` : "搜索结果 - 黄色大片",
    description: keyword
      ? `黄色大片为你找到与“${keyword}”相关的电影资讯与推荐，快速筛选出想看的影片。`
      : "在黄色大片搜索电影、导演与演员，获取最新电影资讯和推荐。",
    keywords: "电影搜索,搜索结果,黄色大片,电影推荐",
  });

  const result = useMemo(
    () => filterAndSortMovies(movies, { ...filters, keyword }),
    [filters, keyword],
  );

  const hotFallback = useMemo(() => [...movies].sort((a, b) => b.heat - a.heat).slice(0, 4), []);

  const pushHistory = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return;
    const next = [trimmed, ...readHistory().filter((item) => item !== trimmed)].slice(0, 8);
    setHistory(next);
    try {
      localStorage.setItem(HISTORY_KEY, JSON.stringify(next));
    } catch {
      /* ignore */
    }
  };

  const handleSubmit = (value: string) => {
    pushHistory(value);
    setSearchParams(value ? { q: value } : {});
  };

  const clearHistory = () => {
    setHistory([]);
    try {
      localStorage.removeItem(HISTORY_KEY);
    } catch {
      /* ignore */
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 md:px-6 md:py-10">
      <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "搜索" }]} />

      <div className="mt-5">
        <h1 className="font-heading text-2xl font-bold text-foreground-50 md:text-3xl">搜索电影</h1>
        <p className="mt-2 text-sm text-foreground-400">
          支持按影片标题、导演、主演与标签搜索，输入关键词即可找到想看的电影。
        </p>
      </div>

      <div className="mt-5 max-w-2xl">
        <SearchBox size="md" defaultValue={keyword} onSubmitKeyword={handleSubmit} />
      </div>

      {history.length > 0 && (
        <div className="mt-5 flex flex-wrap items-center gap-2">
          <span className="flex items-center gap-1.5 text-xs text-foreground-500">
            <i className="ri-history-line text-sm" />
            搜索历史
          </span>
          {history.map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => handleSubmit(item)}
              className="rounded-full bg-background-100 px-3 py-1.5 text-xs text-foreground-300 transition-colors hover:bg-background-200/70 hover:text-foreground-50"
            >
              {item}
            </button>
          ))}
          <button
            type="button"
            onClick={clearHistory}
            className="inline-flex items-center gap-1 rounded-full border border-background-300/60 px-3 py-1.5 text-xs text-foreground-400 transition-colors hover:border-accent-400 hover:text-accent-400"
          >
            <i className="ri-delete-bin-6-line text-sm" />
            清空历史
          </button>
        </div>
      )}

      {keyword && (
        <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-b border-background-200/50 pb-4">
          <p className="text-sm text-foreground-300">
            关键词
            <span className="mx-1 font-semibold text-primary-400">“{keyword}”</span>
            共找到 <span className="font-semibold text-primary-400">{result.length}</span> 部影片
          </p>
          <button
            type="button"
            onClick={() => setSearchParams({})}
            className="inline-flex items-center gap-1 whitespace-nowrap text-xs text-foreground-400 transition-colors hover:text-primary-400"
          >
            <i className="ri-close-line text-sm" />
            清除关键词
          </button>
        </div>
      )}

      {keyword && (
        <div className="mt-5">
          <FilterBar filters={filters} onChange={setFilters} showGenre />
        </div>
      )}

      {!keyword ? (
        <div className="mt-10">
          <h2 className="font-heading text-lg font-bold text-foreground-50">大家都在看</h2>
          <div className="mt-5 grid grid-cols-2 gap-4 md:grid-cols-4">
            {hotFallback.map((movie) => (
              <MovieCard key={movie.id} movie={movie} />
            ))}
          </div>
        </div>
      ) : result.length === 0 ? (
        <div className="mt-10 flex flex-col items-center text-center">
          <i className="ri-search-eye-line text-5xl text-foreground-500" />
          <h2 className="mt-5 font-heading text-xl font-bold text-foreground-50">没有找到相关影片</h2>
          <p className="mt-2 text-sm text-foreground-400">换个关键词试试，或看看下面这些热门推荐。</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link
              to="/list/hot"
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 font-label text-sm uppercase tracking-wide text-foreground-950 transition-colors hover:bg-primary-400"
            >
              <i className="ri-fire-line text-lg" />
              热门电影入口
            </Link>
            <button
              type="button"
              onClick={() => setSearchParams({})}
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-background-300/60 px-6 py-3 font-label text-sm uppercase tracking-wide text-foreground-100 transition-colors hover:border-primary-400 hover:text-primary-400"
            >
              <i className="ri-refresh-line text-lg" />
              重新搜索
            </button>
          </div>
          <div className="mt-10 grid w-full grid-cols-2 gap-4 text-left md:grid-cols-4">
            {hotFallback.map((movie) => (
              <MovieCard key={movie.id} movie={movie} />
            ))}
          </div>
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
          {result.map((movie) => (
            <MovieCard key={movie.id} movie={movie} />
          ))}
        </div>
      )}
    </div>
  );
}